// Angular
import { Injectable } from '@angular/core';

// Third party
import * as _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Right-pane sections the profile panel can show
 */
export type ProfileSection =
  | 'default'
  | 'funding'
  | 'my-profile'
  | 'account-settings'
  | 'account-history'
  | 'bet-results'
  | 'promo'
  | 'responsible-gaming'
  | 'help';

/**
 * State and pure helpers for the Profile panel
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly _showProfile$ = new BehaviorSubject<boolean>(false);
  private readonly _activeSection$ = new BehaviorSubject<ProfileSection>('default');

  showProfile$: Observable<boolean> = this._showProfile$.asObservable();
  activeSection$: Observable<ProfileSection> = this._activeSection$.asObservable();

  get isProfileOpen(): boolean {
    return this._showProfile$.getValue();
  }

  get activeSection(): ProfileSection {
    return this._activeSection$.getValue();
  }

  /**
   * Open profile panel and reset to default section
   * @returns {void}
   */
  openProfile(): void {
    this._activeSection$.next('default');
    this._showProfile$.next(true);
  }

  /**
   * Close profile panel and reset the active section
   * @returns {void}
   */
  closeProfile(): void {
    this._showProfile$.next(false);
    this._activeSection$.next('default');
  }

  /**
   * Toggle the profile panel open/closed
   * @returns {void}
   */
  toggleProfile(): void {
    if (this.isProfileOpen) this.closeProfile();
    else this.openProfile();
  }

  /**
   * Open the profile panel directly on a given section
   * @param {ProfileSection} section Section to show
   * @returns {void}
   */
  openProfileSection(section: ProfileSection): void {
    this._activeSection$.next(section);
    this._showProfile$.next(true);
  }

  /**
   * Change the sub-section shown in the profile panel
   * @param {ProfileSection} section Section identifier
   * @returns {void}
   */
  setActiveSection(section: ProfileSection): void {
    this._activeSection$.next(section);
  }

  /**
   * Build two-letter avatar initials
   * @param {string} firstName User's first name
   * @param {string} lastName User's last name
   * @returns {string} e.g. "J D"
   */
  getUserInitials(firstName?: string, lastName?: string): string {
    const f = (firstName ?? '').charAt(0).toUpperCase();
    const l = (lastName ?? '').charAt(0).toUpperCase();
    return `${f} ${l}`.trim();
  }

  /**
   * Build "FirstName LastName" display string
   * @param {string} firstName User's first name
   * @param {string} lastName User's last name
   * @returns {string} Concatenated full name, trimmed
   */
  getUserDisplayName(firstName?: string, lastName?: string): string {
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
  }

  /**
   * Order promos by end date then start date (ascending)
   * @param {any[]} promos Raw promo response list
   * @returns {any[]} Ordered list
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortByEndAndStart(promos: any[]): any[] {
    return _.orderBy(
      promos,
      [
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (o: any) => new Date(o.enddate),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (o: any) => new Date(o.startdate)
      ],
      ['asc', 'asc']
    );
  }

  /**
   * Count claimable promos/giveaways for the sidebar badge
   * @param {any[]} promos Sorted promo list
   * @returns {number} Count of claimable items
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countAvailablePromoGiveaways(promos: any[]): number {
    const now = moment.utc();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return _.sumBy(promos, (promo: any) => {

      if (!promo?.hasoptin) return 0;

      if (!moment.utc(promo?.nextoptin).isBefore(now)) return 0;

      if (promo?.type === 'Promo' && !promo?.isUsed) {
        return moment.utc(promo?.enddate).isSameOrAfter(now) ? 1 : 0;
      }

      return (
        moment.utc(promo?.startdate).isSameOrBefore(now) &&
        moment.utc(promo?.enddate).isSameOrAfter(now) &&
        !promo?.isUsed
      ) ? 1 : 0;
    });
  }
}
